import React, { useState, useEffect } from 'react';
import { Zap, Plus, Trash2, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import ConditionBuilder from './ConditionBuilder';
import TagInput from '../TagInput';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const FORMS_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGER_OPTIONS = [
    { type: 'manual', label: 'Manual Enrollment', desc: 'Enroll contacts manually or via bulk action' },
    { type: 'contact_created', label: 'Contact Created', desc: 'When a new contact is added to the CRM' },
    { type: 'tag_added', label: 'Tag Added', desc: 'When a specific tag is added to a contact' },
    { type: 'tag_removed', label: 'Tag Removed', desc: 'When a tag is removed from a contact' },
    { type: 'booking_confirmed', label: 'Appointment Booked', desc: 'When a booking is confirmed' },
    { type: 'booking_cancelled', label: 'Appointment Cancelled', desc: 'When a booking is cancelled' },
    { type: 'deal_stage_changed', label: 'Deal Stage Changed', desc: 'When a deal moves to a new stage' },
    { type: 'email_opened', label: 'Email Opened', desc: 'When a tracked email is opened' },
    { type: 'email_clicked', label: 'Email Link Clicked', desc: 'When a link in an email is clicked' },
    { type: 'form_submitted', label: 'Form Submitted', desc: 'When a form/survey is submitted' },
    { type: 'webhook_received', label: 'Webhook Received', desc: 'When an external webhook is received' },
    { type: 'testimonial_submitted', label: 'Testimonial Submitted', desc: 'When a customer submits a testimonial' },
    { type: 'whatsapp_message_received', label: 'WhatsApp Message Received', desc: 'When a WhatsApp message is received from a contact' },
];

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";

export default function TriggerPanel({ triggers = [], onChange, orgId }) {
    const { getAuthHeader } = useAuth();
    const [expanded, setExpanded] = useState(true);
    const [addingTrigger, setAddingTrigger] = useState(false);
    const [orgForms, setOrgForms] = useState([]);
    const [testimonialRequests, setTestimonialRequests] = useState([]);

    useEffect(() => {
        // Fetch org forms for the form_submitted trigger selector
        axios.get(`${FORMS_API}/forms`, { headers: getAuthHeader() })
            .then(res => setOrgForms(res.data || []))
            .catch(() => {});
        // Fetch testimonial requests for trigger selector
        axios.get(`${FORMS_API}/testimonials`, { headers: getAuthHeader() })
            .then(res => setTestimonialRequests(Array.isArray(res.data) ? res.data : res.data?.data || []))
            .catch(() => {});
    }, [getAuthHeader]);

    const addTrigger = (type) => {
        const newTrigger = {
            trigger_id: `trig_${Date.now()}`,
            trigger_type: type,
            config: {},
            filter: { logic: 'and', conditions: [] },
            is_active: true,
        };
        onChange([...triggers, newTrigger]);
        setAddingTrigger(false);
    };

    const updateTrigger = (index, updates) => {
        const updated = [...triggers];
        updated[index] = { ...updated[index], ...updates };
        onChange(updated);
    };

    const removeTrigger = (index) => {
        onChange(triggers.filter((_, i) => i !== index));
    };

    const getTriggerLabel = (type) => TRIGGER_OPTIONS.find(t => t.type === type)?.label || type;

    return (
        <div className="bg-white border-b border-stone-200">
            {/* Header */}
            <button onClick={() => setExpanded(!expanded)}
                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-stone-50 transition-colors">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-stone-700">Triggers</span>
                    <span className="text-xs text-stone-400">({triggers.length})</span>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
            </button>

            {/* Content */}
            {expanded && (
                <div className="px-4 pb-3 space-y-2">
                    {triggers.length === 0 && !addingTrigger && (
                        <p className="text-xs text-stone-400 py-2">No triggers configured. Add a trigger to auto-enroll contacts.</p>
                    )}

                    {/* Existing triggers */}
                    {triggers.map((trigger, i) => (
                        <div key={trigger.trigger_id} className="bg-stone-50 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                                    <span className="text-xs font-medium text-stone-700">{getTriggerLabel(trigger.trigger_type)}</span>
                                </div>
                                <button onClick={() => removeTrigger(i)} className="p-1 text-stone-400 hover:text-red-500">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            {/* Trigger-specific config */}
                            {(trigger.trigger_type === 'tag_added' || trigger.trigger_type === 'tag_removed') && (
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Tag Name</label>
                                    <TagInput
                                        singleSelect
                                        singleValue={trigger.config?.tag || ''}
                                        onSingleChange={tag => updateTrigger(i, { config: { ...trigger.config, tag } })}
                                        placeholder="Search or create a tag..."
                                    />
                                </div>
                            )}

                            {trigger.trigger_type === 'deal_stage_changed' && (
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">To Stage</label>
                                    <select value={trigger.config?.to_stage || ''} onChange={e => updateTrigger(i, { config: { ...trigger.config, to_stage: e.target.value } })}
                                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-md bg-white">
                                        <option value="">Any stage</option>
                                        <option value="Qualified">Qualified</option>
                                        <option value="Proposal Sent">Proposal Sent</option>
                                        <option value="Negotiation">Negotiation</option>
                                        <option value="Closed Won">Closed Won</option>
                                        <option value="Closed Lost">Closed Lost</option>
                                    </select>
                                </div>
                            )}

                            {trigger.trigger_type === 'form_submitted' && (
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Form</label>
                                    <select value={trigger.config?.form_id || ''} onChange={e => updateTrigger(i, { config: { ...trigger.config, form_id: e.target.value || undefined } })}
                                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-md bg-white">
                                        <option value="">Any form</option>
                                        {orgForms.map(f => (
                                            <option key={f.form_id} value={f.form_id}>{f.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {trigger.trigger_type === 'testimonial_submitted' && (
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Testimonial Request</label>
                                    <select value={trigger.config?.testimonial_request_id || ''} onChange={e => updateTrigger(i, { config: { ...trigger.config, testimonial_request_id: e.target.value || undefined } })}
                                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-md bg-white">
                                        <option value="">Any testimonial request</option>
                                        {testimonialRequests.map(r => (
                                            <option key={r.request_id} value={r.request_id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {trigger.trigger_type === 'webhook_received' && orgId && (
                                <div className="bg-white border border-stone-200 rounded-md p-2">
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Webhook URL</label>
                                    <div className="flex items-center gap-1">
                                        <code className="text-[10px] text-stone-600 truncate flex-1">
                                            https://api.nurturecrm.ai/api/campaigns/webhooks/incoming/{orgId}
                                        </code>
                                        <button onClick={() => navigator.clipboard.writeText(`https://api.nurturecrm.ai/api/campaigns/webhooks/incoming/${orgId}`)}
                                            className="p-1 text-stone-400 hover:text-stone-700">
                                            <Copy className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Filter conditions */}
                            <details className="text-xs">
                                <summary className="text-stone-400 cursor-pointer hover:text-stone-600">Filter conditions ({trigger.filter?.conditions?.length || 0})</summary>
                                <div className="mt-2">
                                    <ConditionBuilder
                                        value={trigger.filter || { logic: 'and', conditions: [] }}
                                        onChange={filter => updateTrigger(i, { filter })}
                                    />
                                </div>
                            </details>
                        </div>
                    ))}

                    {/* Add trigger */}
                    {addingTrigger ? (
                        <div className="border border-stone-200 rounded-lg p-2 space-y-1 max-h-48 overflow-y-auto">
                            {TRIGGER_OPTIONS.map(opt => (
                                <button key={opt.type} onClick={() => addTrigger(opt.type)}
                                    className="w-full text-left px-2 py-1.5 rounded hover:bg-stone-50 transition-colors">
                                    <span className="text-xs font-medium text-stone-700">{opt.label}</span>
                                    <span className="text-[10px] text-stone-400 block">{opt.desc}</span>
                                </button>
                            ))}
                            <button onClick={() => setAddingTrigger(false)} className="text-xs text-stone-400 px-2 py-1">Cancel</button>
                        </div>
                    ) : (
                        <button onClick={() => setAddingTrigger(true)}
                            className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 px-2 py-1">
                            <Plus className="w-3 h-3" /> Add trigger
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
